import React, { useState } from "react";
import { graphql, PageProps } from "gatsby";
import {
  BrochureFragment,
  RequestBrochurePageGroupQuery
} from "types/graphql-types";

import { Trans } from "@lingui/macro";
import BrochureLanguageSelector from "components/BrochureLanguageSelector/BrochureLanguageSelector";
import { Button } from "@silversea-ssc/ui-components-react";
import FeaturedContentRow from "components/FeaturedContentRow/FeaturedContentRow";
import FeaturedContentRows from "components/FeaturedContentRow/FeaturedContentRows";
import PageBuilderOpeningText from "components/PageBuilder/PageBuilderOpeningText";
import RequestBrochureModal from "components/RequestBrochureModal/RequestBrochureModal";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import OpenGraph from "components/SEO/OpenGraph";
import { matchesAny } from "src/utils/localization/geolocation";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import {
  getActiveLanguages,
  getDefaultLanguage,
  brochuresForLanguage
} from "./utils";
import { isPresent } from "src/utils/checks";

const BrochureRow: React.FC<{
  brochure: BrochureFragment;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}> = ({ brochure, onClick }) => {
  const button = (
    <Button
      key="brochure-cta"
      variant="primary"
      renderAs="button"
      iconName="download"
      onClick={onClick}
      dataContentPiece="view_brochure_online"
    >
      <Trans>View Online</Trans>
    </Button>
  );
  return (
    <FeaturedContentRow
      title={brochure.title || ""}
      description={brochure.description}
      buttons={[button]}
      image={brochure.thumbnail}
    />
  );
};

const RequestBrochurePageGroup: React.FC<
  PageProps<RequestBrochurePageGroupQuery>
> = ({ data }) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const page = data.page!;
  const thankYouPageURL = data.thankYouPageURL?.path;

  const brochures = (data.brochures?.brochures || []).filter(isPresent);
  const language = useLanguage();
  const geolocation = useGeolocation();

  const languageGroups = React.useMemo(() => {
    return getActiveLanguages(brochures);
  }, [brochures]);
  const defaultLanguage = getDefaultLanguage(languageGroups, language.code);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    defaultLanguage
  );

  const [modalOpened, setModalOpened] = useState<
    | {
        brochure: BrochureFragment;
      }
    | undefined
  >(undefined);

  const filteredBrochures = React.useMemo(
    () =>
      brochuresForLanguage(selectedLanguage, brochures || []).filter((item) => {
        const locationFilters = item.locationFilters?.filter(isPresent);

        if (!locationFilters || !geolocation.countryCode) return true;
        return matchesAny(locationFilters, geolocation.countryCode);
      }),
    [brochures, geolocation.countryCode, selectedLanguage]
  );

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={page.metadata?.openGraph}
        excludeFromSitemap={true}
      />

      <SectionContainer
        spacingTop="regular"
        spacingBottom="compact"
        backgroundColor="white"
      >
        <PageBuilderOpeningText {...page.heading} />
      </SectionContainer>

      <BrochureLanguageSelector
        selectedLanguage={selectedLanguage}
        languages={languageGroups}
        onChange={(language) => setSelectedLanguage(language)}
      />

      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="neutral05"
        dataContentName="brochures"
      >
        <FeaturedContentRows>
          {filteredBrochures.map((b) => (
            <BrochureRow
              key={b._id}
              brochure={b}
              onClick={() => setModalOpened({ brochure: b })}
            />
          ))}
        </FeaturedContentRows>
      </SectionContainer>
      {modalOpened && (
        <RequestBrochureModal
          isOpen={!!modalOpened}
          onRequestClose={() => setModalOpened(undefined)}
          brochure={modalOpened.brochure}
          thankYouPageURL={thankYouPageURL}
        ></RequestBrochureModal>
      )}
    </React.Fragment>
  );
};

export const query = graphql`
  query RequestBrochurePageGroup(
    $parentPageId: String!
    $brochureGroupName: String!
    $languageCode: String
  ) {
    page: sanityRequestBrochure(id: { eq: $parentPageId }) {
      metadata {
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      heading {
        ...PageBuilderOpeningText
      }
    }
    brochures: sanityBrochureGrouping(
      brochures: { elemMatch: { isActive: { eq: true } } }
      name: { eq: $brochureGroupName }
    ) {
      brochures {
        ...Brochure
      }
    }
    thankYouPageURL: sitePage(
      context: {
        sanityId: { regex: "/request-brochure-thankyou-page$/" }
        languageCode: { eq: $languageCode }
      }
    ) {
      path
    }
  }
`;

export default RequestBrochurePageGroup;
